import { ApolloClient, gql, InMemoryCache } from '@apollo/client';
import { GetServerSidePropsContext } from 'next';

import {
  normalizeNavigationChrome,
  type WebflowNavigationChrome,
} from '@/lib/webflow-poc/navigation-chrome';
import {
  normalizeUtilitiesMount,
  type WebflowUtilitiesMountKind,
} from '@/lib/webflow-poc/webflow-utilities-slot';

export type { WebflowNavigationChrome };
export { normalizeNavigationChrome } from '@/lib/webflow-poc/navigation-chrome';
export type { WebflowUtilitiesMountKind };

export type WebflowIntegrationPublicConfig = {
  enabled: boolean;
  siteId: string;
  publishOrigin: string;
  designerUrl: string;
  navigationChrome: WebflowNavigationChrome;
  utilitiesMountKind: WebflowUtilitiesMountKind;
  utilitiesMountName: string;
};

const TENANT_WEBFLOW_FEATURE_FLAG_QUERY = gql`
  query TenantWebflowFeatureFlag {
    tenant {
      result {
        _id
        featureFlags {
          webflow
        }
      }
    }
  }
`;

const WEBFLOW_PUBLIC_CONFIG_QUERY = gql`
  query WebflowIntegrationPublicConfig {
    webflowIntegrationGetPublicConfig {
      result {
        enabled
        siteId
        publishOrigin
        designerUrl
        navigationChrome
        utilitiesMountKind
        utilitiesMountName
      }
    }
  }
`;

const createSsrClient = (context: GetServerSidePropsContext) => {
  const tenantDomain =
    context.req.headers.tenant_domain ||
    context.req.headers['x-forwarded-host'] ||
    context.req.headers.host;

  return {
    client: new ApolloClient({
      ssrMode: true,
      uri: process.env.NEXT_PUBLIC_APOLLO_HTTP_URI,
      cache: new InMemoryCache(),
    }),
    headers: {
      tenant_domain: tenantDomain,
      authorization: `Bearer ${context.req.cookies['accounts%3AaccessToken']}`,
    },
  };
};

const isTenantNotFoundError = (error: unknown): boolean => {
  const networkError = (error as { networkError?: { statusCode?: number } })
    ?.networkError;
  return networkError?.statusCode === 422;
};

/**
 * Lightweight tenant flag check only — does not call any Webflow GraphQL fields.
 */
export const isWebflowFeatureEnabledForRequest = async (
  context: GetServerSidePropsContext
): Promise<boolean> => {
  if (!process.env.NEXT_PUBLIC_APOLLO_HTTP_URI) {
    return false;
  }

  try {
    const { client, headers } = createSsrClient(context);
    const flagQuery = await client.query({
      query: TENANT_WEBFLOW_FEATURE_FLAG_QUERY,
      context: { headers },
      fetchPolicy: 'no-cache',
    });

    return flagQuery.data?.tenant?.result?.featureFlags?.webflow === true;
  } catch (error) {
    if (!isTenantNotFoundError(error)) {
      console.error('Failed to load Webflow feature flag from GraphQL', error);
    }
    return false;
  }
};

/**
 * Loads Webflow public config. Call only after `isWebflowFeatureEnabledForRequest`
 * is true — this hits Webflow GraphQL fields.
 */
export const fetchWebflowIntegrationPublicConfig = async (
  context: GetServerSidePropsContext
): Promise<WebflowIntegrationPublicConfig | null> => {
  if (!process.env.NEXT_PUBLIC_APOLLO_HTTP_URI) {
    return null;
  }

  try {
    const { client, headers } = createSsrClient(context);

    const query = await client.query({
      query: WEBFLOW_PUBLIC_CONFIG_QUERY,
      context: { headers },
      fetchPolicy: 'no-cache',
    });

    const result = query.data?.webflowIntegrationGetPublicConfig?.result;
    if (result?.enabled && result.publishOrigin) {
      const utilitiesMount = normalizeUtilitiesMount(
        result.utilitiesMountKind,
        result.utilitiesMountName
      );
      return {
        enabled: true,
        siteId: result.siteId || '',
        publishOrigin: result.publishOrigin.replace(/\/$/, ''),
        designerUrl: result.designerUrl || '',
        navigationChrome: normalizeNavigationChrome(result.navigationChrome),
        utilitiesMountKind: utilitiesMount.kind,
        utilitiesMountName: utilitiesMount.name,
      };
    }
  } catch (error) {
    // Expected for unknown hosts / health checks / bots — do not spam production logs.
    if (!isTenantNotFoundError(error)) {
      console.error(
        'Failed to load Webflow integration config from GraphQL',
        error
      );
    }
  }

  return null;
};

export const WEBFLOW_PUBLIC_CONFIG_QUERY_DOCUMENT = WEBFLOW_PUBLIC_CONFIG_QUERY;
